/**
 * Binding-attestation wire contract for the WayAI → Rekor service binding.
 *
 * Identity rides these
 * headers and nothing else — the binding forwards NO credential, and the
 * Rekor side trusts them because they arrived on an entrypoint unreachable from
 * outside the Cloudflare account, not because it verified their contents.
 *
 * Two header namespaces meet here and must not be confused:
 *
 * - `X-Rekor-*` is OUTBOUND-ONLY, server-derived, and never integrator-facing.
 *   WayAI mints every one of these from `AuthContext`; an inbound copy is discarded.
 * - `X-WayAI-*` is what WayAI's OWN clients (browser, CLI, MCP) may send. Both of the
 *   inbound ones below are advisory audit provenance — a spoof mislabels an audit row
 *   and grants nothing — which is why they are the only client-settable inputs at this
 *   seam. `X-Rekor-Active-Org`, the client-settable org *hint*, is deliberately retired
 *   — org selection is validated WayAI-side and only the validated result is
 *   attested.
 */

import { z } from 'zod';

// =============================================
// Outbound attestation headers (WayAI → Rekor)
// =============================================

/** `user` | `token` — mirrors Rekor's own branch on auth kind. */
export const REKOR_ACTOR_TYPE_HEADER = 'X-Rekor-Actor-Type';
/** `auth.user_id` (user) or `auth.token_id` (token). */
export const REKOR_ACTOR_ID_HEADER = 'X-Rekor-Actor-Id';
/** Human-readable audit label. Required to be present; may be empty. */
export const REKOR_ACTOR_LABEL_HEADER = 'X-Rekor-Actor-Label';
/** The grant-validated WayAI org UUID. New in step 1; replaces `X-Rekor-Active-Org`. */
export const REKOR_ACTOR_ORG_HEADER = 'X-Rekor-Actor-Org';
/**
 * `1` or `0`, ALWAYS sent. Absence-means-false would make a broken header pipeline
 * indistinguishable from "not an admin" — a silent privilege downgrade instead of the
 * 401 the attested entrypoint owes a malformed attestation.
 */
export const REKOR_ACTOR_ADMIN_HEADER = 'X-Rekor-Actor-Admin';
/** `api` | `cli` | `mcp` — audit `source`, constrained to Rekor's existing allowlist. */
export const REKOR_CLIENT_HEADER = 'X-Rekor-Client';
/** Action provenance, forwarded from the WayAI client. Unchanged from Rekor's own use. */
export const REKOR_TOOL_ID_HEADER = 'X-Rekor-Tool-Id';

// =============================================
// Inbound client hint (WayAI's own clients → WayAI)
// =============================================

/**
 * Action provenance from the WayAI client, mapped onto `X-Rekor-Tool-Id`. Named
 * `X-WayAI-*` rather than `X-Rekor-*` because the Rekor spelling is
 * internal-only: the WayAI `/api` surface is what the CLI and MCP author against.
 *
 * This is the ONLY client-settable value the attestation carries, carved out by name:
 * a spoofed tool id mislabels one audit row and grants nothing (what Rekor drops is stated
 * at `resolveToolId` in `apps/backend/src/rekor/attestation.ts`). No such carve-out exists
 * for the client/`source` header — see `REKOR_CLIENTS`.
 */
export const WAYAI_TOOL_ID_HEADER = 'X-WayAI-Tool-Id';

/**
 * Rekor's audit `source` allowlist.
 *
 * WayAI attests `api` and only `api`. `cli` and `mcp` map to their own surfaces, but
 * WayAI has nothing server-derived to distinguish them: browser, CLI, and MCP all reach
 * `/api/*` with the same credential shapes, so the only available discriminator would be
 * a header the caller sets — which makes the audit `source` a value the request author
 * chooses. A uniformly-`api` label is honestly uninformative; a settable one is actively
 * misleading to whoever reads the audit log during an incident. The other two land when a
 * server-derived marker exists to carry them (the OAuth client id is the natural
 * candidate — the CLI and MCP use different WorkOS apps).
 */
export const REKOR_CLIENTS = ['api', 'cli', 'mcp'] as const;
export type RekorClient = (typeof REKOR_CLIENTS)[number];

export const rekorClientSchema = z.enum(REKOR_CLIENTS);

/** The value WayAI attests today — see `REKOR_CLIENTS`. */
export const WAYAI_ATTESTED_CLIENT: RekorClient = 'api';

/**
 * Rekor caps the accepted provenance header at 128 chars so a raw-REST caller can't
 * bloat its audit log. WayAI forwards subject to the same rules, so an
 * over-long value is dropped here rather than shipped for Rekor to drop.
 */
export const MAX_REKOR_TOOL_ID_LENGTH = 128;

/**
 * Bounds on the forwarded provenance value. Declared once and reused by both the
 * attestation shape below and the builder that drops an out-of-bounds value, so the two
 * cannot disagree after a tightening on either side.
 */
export const rekorToolIdSchema = z.string().trim().min(1).max(MAX_REKOR_TOOL_ID_LENGTH);

// =============================================
// The `/api/data` proxy surface
// =============================================

/**
 * WayAI-side mount for the attested `/v1` proxy, relative to the `/api` router. `data` —
 * not `rekor` — because the path is integrator-facing and the code name stays off
 * those surfaces; it also matches the pinned `data.wayai.pro` hostname.
 */
export const DATA_PROXY_MOUNT = '/data';

/**
 * The absolute path the proxy answers on. `app.ts` mounts from `DATA_PROXY_MOUNT`, the
 * rate-limit config keys off this, and the handler strips this prefix — so a rename moves
 * all three together. Without that, renaming the mount would silently 404 the whole proxy
 * (the prefix strip returns null on a mismatch) with nothing failing to catch it.
 */
export const DATA_PROXY_PREFIX = `/api${DATA_PROXY_MOUNT}`;

/**
 * Everything under the prefix is forwarded beneath `/v1` on the Rekor Worker and
 * nowhere else. Rekor's non-`/v1` surfaces (`/s3`, `/executor`, `/inbound`, `/admin`,
 * `/webhooks`) authenticate on their own terms and must stay unreachable through an
 * attested hop.
 */
export const REKOR_V1_PREFIX = '/v1';

/**
 * The request headers a File upload carries through the proxy: its version check (the
 * `ETag` a read returned) and the metadata and label of the version it writes. Both WayAI
 * hops forward exactly these — the data proxy and the web proxy in front of it — and
 * dropped, the upload still succeeds: unconditionally, without its metadata and label.
 *
 * Cross-repo wire literals: the far side spells
 * the last two `DATA_METADATA_HEADER` and `DATA_LABEL_HEADER` in
 * `apps/rekor/shared/src/constants.ts`. Lowercase, as both hops' allowlists spell names.
 */
export const DATA_FILE_UPLOAD_HEADERS = ['if-match', 'x-data-metadata', 'x-data-label'] as const;

/**
 * The binding-only lifecycle surface — today one operation, the mark-for-erasure that
 * ends a two-phase production hard delete.
 *
 * It sits OUTSIDE `/v1` precisely so `resolveRekorUpstreamUrl` cannot forward to it: the
 * proxy attests any `org:write` caller, including a `way_` agent token, and this operation
 * must only ever be issued by WayAI's own grace timer after a human opened an emailed
 * confirmation link. Reaching it means building the attestation and calling
 * `env.REKOR.fetch` directly — see `apps/backend/src/rekor/base-erasure.ts`.
 *
 * A cross-repo wire literal, like the `X-Rekor-*` header names above: the far side spells it
 * as `LIFECYCLE_PREFIX` in `apps/rekor/backend/src/router.ts`, and the two must not drift.
 */
export const REKOR_LIFECYCLE_PREFIX = '/lifecycle';

/** The one operation on that surface. `base_id` is path-encoded by the caller. */
export function rekorBaseErasurePath(baseId: string): string {
  return `${REKOR_LIFECYCLE_PREFIX}/bases/${encodeURIComponent(baseId)}/erasure`;
}

/**
 * Origin for the binding fetch. Hono routes by path and the host is ignored, so this is
 * cosmetic — but a service-binding fetch still needs an absolute URL, and an
 * internal-looking one keeps a stray log line from reading as a public call.
 */
export const REKOR_BINDING_ORIGIN = 'https://rekor-backend.internal';

/**
 * Org selector for the proxy. `X-Rekor-Active-Org` is retired, so a multi-org user (and
 * a platform admin, who has no default org at all) picks WayAI-side; the value is fed to
 * `resolveOrgFromRequest(auth, explicitOrgId)` and only the grant-validated result is
 * attested. Stripped before forwarding — it is WayAI's selector, not Rekor's
 * input.
 */
export const DATA_PROXY_ORG_QUERY_PARAM = 'org_id';

/**
 * The request-body key carrying the inline entitlement projection on the two
 * provisioning operations. WayAI authors it; Rekor's ATTESTED
 * entrypoint middleware is the only thing allowed to read it, and persists what arrives.
 *
 * ⚠️ It is an attested-only input, like the `X-Rekor-*` headers above and for the same
 * reason: one route tree is mounted on both entrypoints, so a handler reading this key
 * would read it on public traffic too — and a `rec_` caller would then author its own
 * `ops_tenths_included` / `max_records` / `production_bases` into the key declared
 * WayAI-only. That corruption outlives detection, since staleness never fires while
 * a forged `window_resets_at` sits in the future. The Rekor side pins the read to its
 * attested middleware (`rekor:workers/backend/src/middleware/attested-projection.ts`,
 * rekor#894) under its own structural guard; this constant is WayAI's half of the same
 * literal, so the two sides cannot drift apart on a bare string.
 */
export const REKOR_INLINE_PROJECTION_BODY_KEY = 'entitlement';

/**
 * Cap on a buffered `/api/data` request body. THREE shapes are buffered, for TWO different
 * reasons; everything else on the hop streams through with no `/api/*` payload limit, so
 * this bound belongs to these alone.
 *
 * 1. A base upsert and 2. a token mint — buffered because the proxy has to ATTACH the field
 *    above (`REKOR_INLINE_PROJECTION_BODY_KEY`) to them.
 * 3. `POST /v1/:base_id/promote` — buffered to READ `source_base_id` out of it, which the
 *    post-promote credential relink needs. Nothing is attached: the body is forwarded
 *    verbatim. The name `readProvisioningBody` predates this third caller and describes
 *    where the bounded-read machinery lives, not what promote is.
 *
 * All three share this one cap deliberately: it is a memory bound on the isolate, not a
 * per-shape business rule, and a promote body is a small JSON selector nowhere near it.
 *
 * Declared here rather than backend-side for the same reason `MAX_MESSAGE_BODY_BYTES` and
 * `MAX_CI_CONFIG_BODY_BYTES` are: the producer is the CLI (`wayai bases create`,
 * `wayai bases tokens create`, `wayai bases promote`), which cannot import the backend and
 * would otherwise discover the boundary only by round-tripping the body.
 */
export const MAX_PROVISIONING_BODY_BYTES = 256 * 1024;

export const dataProxyQuery = z.object({
  [DATA_PROXY_ORG_QUERY_PARAM]: z.string().uuid().optional(),
});

/**
 * The derived attestation, as a plain header bag, and the SOLE authority on which headers
 * are required: everything but `X-Rekor-Tool-Id` is, and a request missing one is 401 on
 * the Rekor side rather than a fallthrough. Modeled as a schema rather than a bare
 * interface so a test can assert the built headers parse — that rule is the whole reason
 * the Rekor side may skip verification.
 */
export const rekorAttestation = z.object({
  [REKOR_ACTOR_TYPE_HEADER]: z.enum(['user', 'token']),
  [REKOR_ACTOR_ID_HEADER]: z.string().min(1),
  [REKOR_ACTOR_LABEL_HEADER]: z.string(),
  [REKOR_ACTOR_ORG_HEADER]: z.string().uuid(),
  [REKOR_ACTOR_ADMIN_HEADER]: z.enum(['0', '1']),
  [REKOR_CLIENT_HEADER]: rekorClientSchema,
  [REKOR_TOOL_ID_HEADER]: rekorToolIdSchema.optional(),
});

export type RekorAttestation = z.infer<typeof rekorAttestation>;

/**
 * Derived from the schema, never re-listed beside it — a hand-kept copy would let the two
 * disagree about what "required" means, which is the one property standing between a
 * malformed attestation and Rekor's `system`-actor fallback.
 */
export const REQUIRED_REKOR_ATTESTATION_HEADERS = Object.entries(rekorAttestation.shape)
  .filter(([, field]) => !field.isOptional())
  // `Object.entries` widens the keys to `string`; they are the schema's own keys by
  // construction, so narrowing them back is sound rather than an escape hatch.
  .map(([header]) => header) as readonly (keyof RekorAttestation)[];
